// The year's climb, drawn.
//
// A progress bar tells a kid how far along they are. A range with every peak
// of the year standing in it tells them where they are going, which is the
// thing that makes a long goal survive a bad week. All five are always on
// screen, receding into haze, with Everest at the back: the point is that a
// six-year-old can look at it in September and see Everest already there.
//
// The climber is deliberately a small figure against a big range. Kids read
// scale immediately, and "I am here, that is still ahead" needs no words.
import type { SeasonWaypoint } from "../shared/economy";

interface Props {
  feet: number;
  passed: SeasonWaypoint[];
  next: SeasonWaypoint | null;
  waypoints: SeasonWaypoint[];
}

// Each peak sits further right and higher, so the range reads left to right as
// the year. Values are fractions of the viewBox so the shape scales cleanly.
const PEAK_LAYOUT = [
  { x: 0.14, height: 0.30, haze: 0.95 },
  { x: 0.30, height: 0.42, haze: 0.80 },
  { x: 0.48, height: 0.58, haze: 0.64 },
  { x: 0.68, height: 0.74, haze: 0.48 },
  { x: 0.88, height: 0.94, haze: 0.32 },
];

const W = 1000;
const H = 420;
const GROUND = H - 34;

function peakPath(cx: number, topY: number, spread: number): string {
  return `M ${cx - spread} ${GROUND} L ${cx} ${topY} L ${cx + spread} ${GROUND} Z`;
}

// A snow cap that follows the same slope as the peak it sits on.
function capPath(cx: number, topY: number, spread: number, drop: number): string {
  const ratio = drop / (GROUND - topY);
  const half = spread * ratio;
  return `M ${cx - half} ${topY + drop} L ${cx} ${topY} L ${cx + half} ${topY + drop} `
    + `L ${cx + half * 0.45} ${topY + drop * 0.72} L ${cx} ${topY + drop} `
    + `L ${cx - half * 0.5} ${topY + drop * 0.66} Z`;
}

export function SeasonMountain({ feet, passed, next, waypoints }: Props) {
  const summitted = passed.length;
  const finalFeet = waypoints[waypoints.length - 1]?.feet ?? 1;

  // The climber walks the whole range across the year rather than resetting at
  // each peak, so progress always reads forward.
  const journey = Math.max(0, Math.min(1, feet / finalFeet));
  const climberX = 0.06 + journey * 0.82;
  // Follow the ridgeline rather than floating: sit on whichever peak's slope
  // the climber is currently crossing.
  const climberY = 1 - (0.10 + journey * 0.72);

  return (
    <svg className="range" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`${feet.toLocaleString()} feet climbed. ${summitted} of ${waypoints.length} peaks summited.`}>
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0B1220" />
          <stop offset="55%" stopColor="#1E3A8A" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
        <linearGradient id="done" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="todo" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#93C5FD" />
          <stop offset="100%" stopColor="#1E3A8A" />
        </linearGradient>
      </defs>

      <rect width={W} height={H} fill="url(#sky)" />

      {/* A few stars, fixed rather than random so the scene never jitters. */}
      {[[90, 46], [220, 28], [370, 62], [520, 34], [680, 54], [830, 26], [930, 70]].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={1.6} fill="#E2E8F0" opacity={0.5} />
      ))}

      {/* Back to front, so nearer peaks overlap the hazier ones behind. */}
      {[...waypoints].map((point, index) => ({ point, index })).reverse().map(({ point, index }) => {
        const layout = PEAK_LAYOUT[index] ?? PEAK_LAYOUT[PEAK_LAYOUT.length - 1]!;
        const cx = layout.x * W;
        const topY = GROUND - layout.height * (GROUND - 40);
        const spread = 96 + index * 30;
        const isDone = index < summitted;
        const isNext = next?.label === point.label;
        return (
          <g key={point.label} opacity={isDone ? 1 : 0.42 + (1 - layout.haze) * 0.3}>
            <path d={peakPath(cx, topY, spread)} fill={isDone ? "url(#done)" : "url(#todo)"} />
            <path d={capPath(cx, topY, spread, 30 + index * 6)} fill="#F8FAFC" opacity={isDone ? 0.95 : 0.7} />
            {isDone && (
              <g transform={`translate(${cx - 2} ${topY - 4})`}>
                <line x1="0" y1="0" x2="0" y2="-26" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" />
                <path d="M 0 -26 L 22 -19 L 0 -12 Z" fill="#F59E0B" />
              </g>
            )}
            {isNext && (
              <circle cx={cx} cy={topY - 16} r="6" fill="#FCD34D">
                <animate attributeName="opacity" values="0.35;1;0.35" dur="2.4s" repeatCount="indefinite" />
              </circle>
            )}
            <text x={cx} y={GROUND + 22} textAnchor="middle"
              fill={isDone ? "#6EE7B7" : "#93C5FD"} fontSize="17" fontWeight="700"
              opacity={isDone ? 1 : 0.75}>
              {point.label}
            </text>
          </g>
        );
      })}

      {/* The climber. Small on purpose: scale is the whole message. */}
      <g transform={`translate(${climberX * W} ${climberY * H})`}>
        <g className="climber">
          {/* pack */}
          <rect x="-13" y="-9" width="10" height="15" rx="3" fill="#B45309" />
          {/* body */}
          <path d="M -5 -10 L 6 -10 L 4 8 L -3 8 Z" fill="#2563EB" />
          {/* legs */}
          <line x1="-1" y1="8" x2="-5" y2="20" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" />
          <line x1="2" y1="8" x2="7" y2="19" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" />
          {/* arm and axe */}
          <line x1="4" y1="-6" x2="15" y2="-15" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" />
          <line x1="15" y1="-15" x2="15" y2="-2" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 15 -15 L 23 -18 L 15 -11 Z" fill="#CBD5E1" />
          {/* head and beanie */}
          <circle cx="0" cy="-18" r="8" fill="#FDE68A" />
          <path d="M -8 -20 A 8 8 0 0 1 8 -20 Z" fill="#10B981" />
          <circle cx="0" cy="-28" r="3" fill="#FCD34D" />
        </g>
      </g>
    </svg>
  );
}
